import { FolderOpen } from 'lucide-react'
import { TwoFAIndicator } from '@/components/sv/two-fa-indicator'
import { Muted } from '@/components/sv/typographies'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { getProjectRoleLabel, getServiceRoleLabel } from '@/lib/rbac'
import type {
  Row,
  UsersTableMeta,
} from '@/routes/user-management/users-table/types'
import type { CellContext } from '@tanstack/react-table'

export { ActionsCell } from './actions-cell'

export const NameCell = ({ row, table }: CellContext<Row, unknown>) => {
  const name = row.original?.user?.fullName
  const meta = table.options.meta as UsersTableMeta
  const isAuthenticatedUser = row.original.user.id === meta?.authenticatedIdUser

  return (
    <div className="py-3 px-4 flex items-center gap-2 font-medium">
      <Avatar className="h-4 w-4 rounded-lg">
        <AvatarImage
          src={row.original?.user?.image ?? ''}
          alt={row.original?.user?.fullName ?? ''}
        />
        <AvatarFallback className="rounded-lg">
          {`${row.original?.user?.firstName?.[0] ?? ''}${row.original?.user?.lastName?.[0] ?? ''}`.toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 items-center gap-2">
        <span
          title={name ? name : 'Pending invitation'}
          className="block max-w-[400px] overflow-ellipsis whitespace-nowrap overflow-hidden"
        >
          {name ? name : 'Pending invitation'}
        </span>
        {isAuthenticatedUser ? (
          <Badge variant="outline" className="shrink-0">
            You
          </Badge>
        ) : null}
      </div>
    </div>
  )
}

export const EmailCell = ({ row }: CellContext<Row, unknown>) => (
  <Muted
    title={row.original?.user?.email ?? ''}
    className="px-2 overflow-ellipsis whitespace-nowrap overflow-hidden"
  >
    {row.original?.user?.email}
  </Muted>
)

export const TwoFaCell = ({ row }: CellContext<Row, unknown>) => (
  <TwoFAIndicator
    type={row.original?.user?.fullName ? row.original.user.type2FA : 'na'}
  />
)

export const RoleCell = ({ row, table }: CellContext<Row, unknown>) => {
  const meta = table.options.meta as UsersTableMeta
  const projects = meta?.projects ?? []
  const services = meta?.services ?? []
  const showProjectAccessTooltip = meta?.showProjectAccessTooltip ?? true

  const roleMap: Record<string, string> = {
    'kinsta-superadmin': 'Kinsta superadmin',
    'kinsta-betatester': 'Kinsta betatester',
    'kinsta-internal': 'Kinsta internal',
    'kinsta-finance': 'Kinsta finance',
    'company-owner': 'Company owner',
    'company-admin': 'Company admin',
    'company-developer': 'Company developer',
    'company-billing': 'Company billing',
  }

  const companyCapability = (row.original?.capabilities ?? []).find(
    (c) => c.type === 'company'
  )
  const projectCapabilities = (row.original?.capabilities ?? []).filter(
    (c) => c.idProject
  )
  const serviceCapabilities = (row.original?.capabilities ?? []).filter(
    (c) =>
      c.idApp ||
      c.idDatabase ||
      c.idStaticSite ||
      c.idObjectStorage ||
      c.idLoadBalancer
  )

  // If user has a company-level role, show that
  if (companyCapability?.idRole) {
    const role = roleMap?.[companyCapability.idRole]
    return (
      <Muted className="px-2 overflow-ellipsis whitespace-nowrap overflow-hidden">
        {role ?? 'Service-level access'}
      </Muted>
    )
  }

  if (projectCapabilities.length > 0) {
    const projectAccess = projectCapabilities
      .map((cap) => {
        const project = projects.find((p) => p.id === cap.idProject)
        return {
          id: cap.idProject,
          name: project?.displayName,
          role: getProjectRoleLabel(cap.idRole),
        }
      })
      .filter((project) => Boolean(project.name))

    if (projectAccess.length === 1 && !showProjectAccessTooltip) {
      return (
        <Muted className="px-2 overflow-ellipsis whitespace-nowrap overflow-hidden">
          {projectAccess[0]?.role}
        </Muted>
      )
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Muted className="w-min mx-2 cursor-help border-b border-dashed border-muted-foreground">
            Project-level access
          </Muted>
        </TooltipTrigger>
        <TooltipContent>
          <ul className="grid gap-y-1">
            {projectAccess.slice(0, 3).map((project) => (
              <li key={project.id} className="flex items-center gap-1">
                <FolderOpen className="size-4" aria-hidden="true" />
                {project.name} - {project.role}
              </li>
            ))}
            {projectAccess.length > 3 && (
              <li className="text-center">
                and {projectAccess.length - 3} more
              </li>
            )}
          </ul>
        </TooltipContent>
      </Tooltip>
    )
  }

  if (serviceCapabilities.length > 0) {
    const serviceAccess = serviceCapabilities
      .map((capability) => {
        const idService =
          capability.idApp ||
          capability.idDatabase ||
          capability.idStaticSite ||
          capability.idObjectStorage ||
          capability.idLoadBalancer
        const service = services.find((item) => item.id === idService)

        return {
          id: idService,
          name: service?.displayName,
          role: getServiceRoleLabel(capability.idRole),
        }
      })
      .filter((service) => Boolean(service.id && service.name))

    if (serviceAccess.length > 0) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Muted className="w-min mx-2 cursor-help border-b border-dashed border-muted-foreground whitespace-nowrap">
              Service-level access
            </Muted>
          </TooltipTrigger>
          <TooltipContent>
            <ul className="grid gap-y-1">
              {serviceAccess.slice(0, 3).map((service) => (
                <li key={service.id} className="flex items-center gap-1">
                  {service.name} - {service.role}
                </li>
              ))}
              {serviceAccess.length > 3 && (
                <li className="text-center">
                  and {serviceAccess.length - 3} more
                </li>
              )}
            </ul>
          </TooltipContent>
        </Tooltip>
      )
    }
  }

  return (
    <Muted className="px-2 overflow-ellipsis whitespace-nowrap overflow-hidden">
      Service-level access
    </Muted>
  )
}
